
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { TrendingUp, Calendar, Target, Brain } from 'lucide-react';
import { kgToLbs } from '@/lib/weightConversion';

const chartConfig = {
  actual: {
    label: "Actual",
    color: "hsl(var(--chart-1))",
  },
  predicted: {
    label: "Predicted",
    color: "hsl(var(--chart-2))",
  },
};

export const TrendPrediction: React.FC = () => {
  const { user } = useAuth();

  const { data: predictionData, isLoading } = useQuery({
    queryKey: ['trend-prediction'],
    queryFn: async () => {
      // Get recent body composition data
      let { data: bodyData, error } = await supabase
        .from('body_composition')
        .select('recorded_date, total_weight, body_fat_percentage')
        .order('recorded_date', { ascending: true })
        .limit(10);

      // Fallback to sample data
      if (!bodyData || bodyData.length === 0) {
        const { data: sampleData } = await supabase
          .from('body_composition')
          .select('recorded_date, total_weight, body_fat_percentage')
          .eq('user_id', '00000000-0000-0000-0000-000000000001')
          .order('recorded_date', { ascending: true })
          .limit(10);
        bodyData = sampleData || [];
      }

      if (error) throw error;
      if (!bodyData || bodyData.length < 3) return null;

      // Prepare historical data
      const historicalData = bodyData.map((entry, index) => ({
        date: new Date(entry.recorded_date).toLocaleDateString(),
        dayIndex: index,
        weight: entry.total_weight ? kgToLbs(entry.total_weight) : 0,
        bodyFat: entry.body_fat_percentage || 0,
        type: 'actual' as const,
      }));

      // Simple linear regression for prediction
      const weightTrend = calculateLinearRegression(historicalData.map(d => d.dayIndex), historicalData.map(d => d.weight));
      const bodyFatTrend = calculateLinearRegression(historicalData.map(d => d.dayIndex), historicalData.map(d => d.bodyFat));

      // Apply conservative limits to trends (max 2 lbs/week weight loss, 0.5%/week body fat change)
      const maxWeightChangePerWeek = 2; // lbs
      const maxBodyFatChangePerWeek = 0.5; // percentage
      
      const conservativeWeightSlope = Math.sign(weightTrend.slope) * Math.min(
        Math.abs(weightTrend.slope * 7), 
        maxWeightChangePerWeek
      ) / 7;
      
      const conservativeBodyFatSlope = Math.sign(bodyFatTrend.slope) * Math.min(
        Math.abs(bodyFatTrend.slope * 7), 
        maxBodyFatChangePerWeek
      ) / 7;

      // Generate future predictions (next 4 weeks)
      const predictions = [];
      const lastDayIndex = historicalData.length - 1;
      const lastWeight = historicalData[historicalData.length - 1].weight;
      const lastBodyFat = historicalData[historicalData.length - 1].bodyFat;
      const weeksToPredict = 4;
      
      for (let week = 1; week <= weeksToPredict; week++) {
        const futureDayIndex = lastDayIndex + (week * 7);
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + (week * 7));
        
        // Use conservative slopes and start from last actual values
        const predictedWeight = lastWeight + (conservativeWeightSlope * week * 7);
        const predictedBodyFat = lastBodyFat + (conservativeBodyFatSlope * week * 7);
        
        predictions.push({
          date: futureDate.toLocaleDateString(),
          dayIndex: futureDayIndex,
          weight: Math.max(predictedWeight, lastWeight * 0.8), // Don't predict more than 20% weight loss
          bodyFat: Math.max(predictedBodyFat, Math.max(lastBodyFat * 0.7, 5)), // Don't go below 5% body fat
          type: 'predicted' as const,
        });
      }

      return {
        historical: historicalData,
        predictions,
        trends: {
          weightTrend: weightTrend.slope > 0 ? 'increasing' : 'decreasing',
          bodyFatTrend: bodyFatTrend.slope > 0 ? 'increasing' : 'decreasing',
          weightChange: Math.abs(weightTrend.slope * 7), // weekly change
          bodyFatChange: Math.abs(bodyFatTrend.slope * 7), // weekly change
        },
        combined: [...historicalData, ...predictions],
      };
    },
  });

  // AI-powered insights query
  const { data: aiInsights, isLoading: insightsLoading } = useQuery({
    queryKey: ['ai-insights', predictionData?.historical?.length, predictionData?.trends],
    queryFn: async () => {
      if (!predictionData) return null;
      
      try {
        const { data, error } = await supabase.functions.invoke('generate-fitness-insights', {
          body: {
            weightTrend: predictionData.trends.weightTrend,
            bodyFatTrend: predictionData.trends.bodyFatTrend,
            weightChange: predictionData.trends.weightChange,
            bodyFatChange: predictionData.trends.bodyFatChange,
            historical: predictionData.historical,
            predictions: predictionData.predictions,
          },
        });

        if (error) {
          console.error('Error generating AI insights:', error);
          return null;
        }

        return data;
      } catch (error) {
        console.error('Failed to generate AI insights:', error);
        return null;
      }
    },
    enabled: !!predictionData,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const calculateLinearRegression = (x: number[], y: number[]) => {
    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.map((xi, i) => xi * y[i]).reduce((a, b) => a + b, 0);
    const sumX2 = x.map(xi => xi * xi).reduce((a, b) => a + b, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return { slope, intercept };
  };

  const getTrendIcon = (trend: string) => {
    return trend === 'increasing' ? 
      <TrendingUp className="h-4 w-4 text-red-500" /> : 
      <TrendingUp className="h-4 w-4 text-green-500 rotate-180" />;
  };

  const getTrendColor = (trend: string, metric: string) => {
    if (metric === 'weight') {
      return trend === 'decreasing' ? 'text-green-600' : 'text-red-600';
    } else { // body fat
      return trend === 'decreasing' ? 'text-green-600' : 'text-red-600';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Trend Predictions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Analyzing trends...</div>
        </CardContent>
      </Card>
    );
  }

  if (!predictionData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-500" />
            Trend Predictions
          </CardTitle>
          <CardDescription>AI-powered predictions based on your progress</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            Need at least 3 data points to generate predictions
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-purple-500" />
          Trend Predictions
        </CardTitle>
        <CardDescription>AI-powered predictions based on your progress</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Trend Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Weight Trend</span>
                {getTrendIcon(predictionData.trends.weightTrend)}
              </div>
              <div className={`text-lg font-bold ${getTrendColor(predictionData.trends.weightTrend, 'weight')}`}>
                {predictionData.trends.weightTrend === 'increasing' ? '+' : '-'}{predictionData.trends.weightChange.toFixed(1)}lbs
              </div>
              <div className="text-xs text-gray-500">per week</div>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Body Fat Trend</span>
                {getTrendIcon(predictionData.trends.bodyFatTrend)}
              </div>
              <div className={`text-lg font-bold ${getTrendColor(predictionData.trends.bodyFatTrend, 'bodyFat')}`}>
                {predictionData.trends.bodyFatTrend === 'increasing' ? '+' : '-'}{predictionData.trends.bodyFatChange.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500">per week</div>
            </div>
          </div>

          {/* Prediction Chart */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-4 w-4" />
              <span className="text-sm font-medium">4-Week Prediction</span>
              <Badge variant="outline" className="text-xs">Weight (lbs)</Badge>
            </div>
            
            <ChartContainer config={chartConfig} className="h-48 md:h-64 lg:h-80 xl:h-96 w-full">
              <LineChart data={predictionData.combined}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <ChartTooltip content={<ChartTooltipContent />} />
                
                <ReferenceLine 
                  x={predictionData.historical[predictionData.historical.length - 1]?.date} 
                  stroke="#666" 
                  strokeDasharray="2 2" 
                />
                
                <Line
                  dataKey="weight"
                  stroke="var(--color-actual)"
                  strokeWidth={2}
                  dot={{ fill: "var(--color-actual)", r: 3 }}
                  connectNulls={false}
                  name="Weight (lbs)"
                />
              </LineChart>
            </ChartContainer>
          </div>

          {/* AI-Powered Insights */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-purple-500" />
              <h4 className="font-medium text-sm">AI-Powered Insights</h4>
              {insightsLoading && (
                <Badge variant="outline" className="text-xs animate-pulse">
                  Analyzing...
                </Badge>
              )}
              {aiInsights?.source === 'ai' && (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                  AI Generated
                </Badge>
              )}
            </div>
            
            <div className="space-y-2 text-sm text-gray-600">
              {insightsLoading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6"></div>
                </div>
              ) : aiInsights?.insights ? (
                aiInsights.insights.map((insight: string, index: number) => (
                  <p key={index} className="text-gray-700">
                    {insight}
                  </p>
                ))
              ) : (
                // Fallback to basic insights if AI fails
                <>
                  <p>
                    • Based on current trends, your weight will {predictionData.trends.weightTrend === 'increasing' ? 'increase' : 'decrease'} by approximately {(predictionData.trends.weightChange * 4).toFixed(1)}lbs over the next 4 weeks
                  </p>
                  <p>
                    • Your body fat percentage is trending {predictionData.trends.bodyFatTrend}, changing by about {predictionData.trends.bodyFatChange.toFixed(1)}% per week
                  </p>
                  <p className="text-blue-600">
                    • Keep in mind these are estimates based on current patterns - your actual results may vary based on training and nutrition changes
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
